import React from 'react';
import {StyleSheet, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import PompiLogo from '../../assets/PompiImage.svg';
import AuthBackdrop from '../AuthBackdrop';
import {colors, radii, spacing, typography} from '../../config/theme';
import {
  DESKTOP_AUTH_COLUMN_WIDTH,
  useDesktopLayout,
} from '../../utility/useDesktopLayout';

const FEATURES = [
  'root.Desktop.featureRoute',
  'root.Desktop.featureScan',
  'root.Desktop.featureRewards',
];

// Desktop web frame for the login, sign-up and onboarding flow: a brand panel
// on the left and the (phone-shaped) flow in a column on the right. On mobile
// it renders the flow full screen, with the same tree so nothing remounts
// when the window crosses the breakpoint.
const DesktopAuthLayout = ({children}) => {
  const isDesktop = useDesktopLayout();
  const {t} = useTranslation();
  return (
    <View style={styles.row}>
      <View
        style={[styles.brand, !isDesktop && styles.hidden]}
        accessibilityElementsHidden={!isDesktop}>
        {isDesktop && <AuthBackdrop glowY={0.4} />}
        <View style={styles.brandContent}>
          <PompiLogo width={180} height={172} />
          <Text style={styles.headline}>{t('root.Desktop.headline')}</Text>
          <Text style={styles.tagline}>{t('root.Login.tagline')}</Text>
          <View style={styles.features}>
            {FEATURES.map(key => (
              <View key={key} style={styles.feature}>
                <View style={styles.featureDot} />
                <Text style={styles.featureText}>{t(key)}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
      <View style={[styles.flow, isDesktop && styles.flowDesktop]}>
        {children}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  row: {flex: 1, flexDirection: 'row', backgroundColor: colors.background},
  brand: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: colors.mintWash,
  },
  hidden: {display: 'none'},
  brandContent: {
    maxWidth: 440,
    paddingHorizontal: spacing.xl,
    alignItems: 'center',
  },
  headline: {
    ...typography.displayLg,
    color: colors.brandDeep,
    textAlign: 'center',
    marginTop: spacing.xl,
  },
  tagline: {
    ...typography.bodyLg,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: spacing.sm,
  },
  features: {
    marginTop: spacing.xl,
    gap: spacing.md,
    alignSelf: 'stretch',
    padding: spacing.lg,
    borderRadius: radii.card,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.mintLine,
  },
  feature: {flexDirection: 'row', alignItems: 'center', gap: spacing.md},
  featureDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.secondary,
  },
  featureText: {...typography.bodyMd, color: colors.textStrong, flex: 1},
  flow: {flex: 1, overflow: 'hidden'},
  flowDesktop: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: DESKTOP_AUTH_COLUMN_WIDTH,
    width: DESKTOP_AUTH_COLUMN_WIDTH,
    borderLeftWidth: 1,
    borderLeftColor: colors.border,
    backgroundColor: colors.surface,
  },
});

export default DesktopAuthLayout;
